import React, { useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { Accordion, AccordionDetails } from '@material-ui/core';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';

import { updateEmployee } from '../../../employees/actions/update-employee';
import { bulkRetryMessage } from '../../../messages/actions/bulk-retry-message';
import { retryMessage } from '../../../messages/actions/retry-message';

import { useApi } from '../../../api/use-api';
import { UiButton } from '../../../ui/ui-button';
import { UiDialog } from '../../../ui/ui-dialog';
import { UiProgressBar } from '../../../ui/ui-progress-bar';

import { LegacyFailedMessageRow } from './components/legacy-failed-message-row';
import { LegacyInvalidPhoneNumberRow } from './components/legacy-invalid-phone-number-row';
import { LegacyPendingMessageRow } from './components/legacy-pending-message-row';

import { employeeNameFromSimpleContact } from '../../../employees/utils/employee-name';
import { isTruthy } from '../../../util/is-truthy';
import { ICON_NAMES, MaterialDesignIcon } from '../../../util/material-design-icons';
import { isPhoneNumber } from '../../../util/string-validators';
import { confirm } from '../../../widgets/confirmation-dialog';
import { LoadingMessage } from '../../../widgets/loading-message';

// NOTE: This array is duplicated in twilio-client.ts on the server, and it should be kept up to date with it
const INVALID_PHONE_ERROR_CODES = [
  30005, // Message Delivery - Unknown destination handset (https://www.twilio.com/docs/api/errors/30005)
  30006, // Landline or unreachable carrier (https://www.twilio.com/docs/api/errors/30006)
  21610, // Attempt to send to unsubscribed recipient (https://www.twilio.com/docs/api/errors/21610)
  21211, // Invalid 'To' Phone Number (https://www.twilio.com/docs/api/errors/21211)
];

type InputProps = {
  announcementId: number;
  onClose: () => void;
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    updatePhoneNumber: (employeeId: number, phoneNumber: string) =>
      dispatch(
        updateEmployee(employeeId, {
          Contact: { Person: { phoneNumber } },
        }),
      ),
    retrySendMessage: (messageId: number) => dispatch(retryMessage(messageId)),
    bulkRetrySendMessages: (messageIds: Array<number>) => dispatch(bulkRetryMessage(messageIds)),
  },
});

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & DispatchProps;

export const LegacyAnnouncementStatusDialog = connect(
  undefined,
  mapDispatchToProps,
)((props: Props) => {
  const { announcementId, onClose, actions } = props;

  const [isSending, setIsSending] = useState<boolean>(false);
  const [resentMessageIds, setResentMessageId] = useState<Array<number>>([]);

  const { data, loading } = useApi<AnnouncementDeliverabilityDetail[]>({
    url: `/announcements/${announcementId}/deliverability`,
  });

  const { successfulDetails, invalidPhoneDetails, failedDetails, pendingDetails } = (
    data || []
  ).reduce(
    (acc, announcementDetail) => {
      if (announcementDetail.isPending) {
        acc.pendingDetails.push(announcementDetail);
      } else if (!announcementDetail.isFailed) {
        acc.successfulDetails.push(announcementDetail);
      } else if (
        !isPhoneNumber(announcementDetail.phoneNumber) ||
        (announcementDetail.errorCode &&
          INVALID_PHONE_ERROR_CODES.includes(announcementDetail.errorCode))
      ) {
        acc.invalidPhoneDetails.push(announcementDetail);
      } else {
        acc.failedDetails.push(announcementDetail);
      }
      return acc;
    },
    {
      successfulDetails: [] as Array<AnnouncementDeliverabilityDetail>,
      invalidPhoneDetails: [] as Array<AnnouncementDeliverabilityDetail>,
      failedDetails: [] as Array<AnnouncementDeliverabilityDetail>,
      pendingDetails: [] as Array<AnnouncementDeliverabilityDetail>,
    },
  );

  const handleRetrySendMessage = async (messageDetail: AnnouncementDeliverabilityDetail) => {
    if (!messageDetail.messageId) {
      return false;
    }

    // If this employee was deleted or terminated, have the user confirm that they want to send
    // the message before retrying
    if (!messageDetail.canRetrySend) {
      const employeeName = employeeNameFromSimpleContact({
        nickname: messageDetail.nickname,
        firstName: messageDetail.firstName,
        lastName: messageDetail.lastName,
      });
      if (
        await confirm(
          `${employeeName} is not a current employee, are you sure you want to resend this announcement to them?`,
        )
      ) {
        actions.retrySendMessage(messageDetail.messageId);
        setResentMessageId(resentMessageIds.concat([messageDetail.messageId]));
        return true;
      }
      return false;
    }

    actions.retrySendMessage(messageDetail.messageId);
    setResentMessageId(resentMessageIds.concat([messageDetail.messageId]));
    return true;
  };

  const handleRetryAllFailed = async () => {
    setIsSending(true);

    const allFailedMessageIds = failedDetails
      .concat(invalidPhoneDetails)
      .filter(
        (message) =>
          message.messageId &&
          !resentMessageIds.includes(message.messageId) &&
          message.canRetrySend,
      )
      .map((m) => m.messageId)
      .filter(isTruthy);

    await actions.bulkRetrySendMessages(allFailedMessageIds);

    setIsSending(false);
    onClose();
  };

  return (
    <UiDialog onClose={onClose} title="Announcement Status Summary" size="sm">
      {loading ? (
        <UiDialog.Content>
          <LoadingMessage />
        </UiDialog.Content>
      ) : (
        <>
          <UiDialog.Content>
            <UiProgressBar
              className="u-margin-bottom-lg"
              successfulCount={successfulDetails.length}
              failedCount={failedDetails.length + invalidPhoneDetails.length}
              totalCount={(data || []).length}
              actionText="delivered"
            />
            <Accordion disabled={!invalidPhoneDetails.length}>
              <AccordionSummary
                expandIcon={<MaterialDesignIcon iconName={ICON_NAMES.EXPAND_MORE} />}
              >
                <div>
                  Invalid phone numbers for {invalidPhoneDetails.length} employee
                  {invalidPhoneDetails.length === 1 ? '' : 's'}
                </div>
              </AccordionSummary>
              <AccordionDetails className="u-display-flex u-flexWrap-wrap">
                <Table>
                  <TableBody>
                    {invalidPhoneDetails.map((invalidPhoneDetail) => (
                      <LegacyInvalidPhoneNumberRow
                        key={invalidPhoneDetail.employeeId}
                        messageDetail={invalidPhoneDetail}
                        updatePhoneNumber={actions.updatePhoneNumber}
                        retryMessage={handleRetrySendMessage}
                      />
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
            <Accordion disabled={!failedDetails.length}>
              <AccordionSummary
                expandIcon={<MaterialDesignIcon iconName={ICON_NAMES.EXPAND_MORE} />}
              >
                <div>
                  Failed to send to {failedDetails.length} employee
                  {failedDetails.length === 1 ? '' : 's'}
                </div>
              </AccordionSummary>
              <AccordionDetails className="u-display-flex u-flexWrap-wrap">
                <Table>
                  <TableBody>
                    {failedDetails.map((failedDetail) => (
                      <LegacyFailedMessageRow
                        key={failedDetail.employeeId}
                        messageDetail={failedDetail}
                        retryMessage={handleRetrySendMessage}
                      />
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
            <Accordion disabled={!pendingDetails.length}>
              <AccordionSummary
                expandIcon={<MaterialDesignIcon iconName={ICON_NAMES.EXPAND_MORE} />}
              >
                <div>
                  Pending delivery to {pendingDetails.length} employee
                  {pendingDetails.length === 1 ? '' : 's'}
                </div>
              </AccordionSummary>
              <AccordionDetails className="u-display-flex u-flexWrap-wrap">
                <Table>
                  <TableBody>
                    {pendingDetails.map((pendingDetail) => (
                      <LegacyPendingMessageRow
                        key={pendingDetail.employeeId}
                        messageDetail={pendingDetail}
                      />
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          </UiDialog.Content>
          <UiDialog.Actions>
            <UiButton onClick={onClose} variant="secondary">
              Close
            </UiButton>
            <UiButton
              onClick={handleRetryAllFailed}
              loading={isSending}
              disabled={!failedDetails.length && !invalidPhoneDetails.length}
            >
              Retry Failed
            </UiButton>
          </UiDialog.Actions>
        </>
      )}
    </UiDialog>
  );
});
